// src/components/solution_sections/SolutionHeroSection.tsx
import React from 'react';  
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';

import { MultiAgentSolutionData_V10, AgentHubPageViewMode, OwnerContext } from '@dopomogai/types';
import { VisualElement } from '@dopomogai/ui';
import { Badge } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { IconButton } from '@dopomogai/ui';
import { renderIcon } from '@dopomogai/utils';

interface SolutionHeroSectionProps {
  data: MultiAgentSolutionData_V10['heroSection'];
  viewMode: AgentHubPageViewMode;
  ownerContext?: OwnerContext;
  onAction: (actionType: string, payload?: any) => void;
  id?: string;
}

export const SolutionHeroSection = React.forwardRef<HTMLDivElement, SolutionHeroSectionProps>(
  ({ data, viewMode, ownerContext, onAction, id }, ref) => {
    const { t } = useTranslation('common'); 
    const isOwnerView = viewMode === 'owner_dashboard';
    
    const textVariants = {
      hidden: { opacity: 0, y: 20 },
      visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.08, duration: 0.4, ease: "easeOut" }
      })
    };

    return (
      <section id={id} ref={ref} className="bg-white rounded-xl border-2 border-black shadow-lg overflow-hidden mb-8">
        <div className="flex flex-col lg:flex-row">
          
          <div className="lg:w-2/5 xl:w-5/12 relative flex items-center justify-center p-6 md:p-8 lg:p-10 bg-slate-50 border-b-2 border-black lg:border-b-0 lg:border-r-2 lg:border-black">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1, ease: [0.6, -0.05, 0.01, 0.99] }}
              className="aspect-square w-full max-w-md lg:max-w-none relative group border-2 border-black rounded-xl overflow-hidden shadow-[8px_8px_0_0_rgb(17,24,39)]"
            >
              <VisualElement
                visual={data.heroVisual}
                className="transform transition-transform duration-500 ease-out group-hover:scale-105"
              />
              {isOwnerView && (
                <div className="absolute top-3 right-3">
                  <IconButton
                    icon="edit"
                    ariaLabelKey="heroSection.editVisualAria"
                    onClick={() => onAction('EDIT_SOLUTION_HERO_VISUAL', {})}
                    variant="secondary"
                    size="sm"
                    className="!rounded-lg bg-white/80 backdrop-blur-sm hover:bg-white"
                    tooltipKey="heroSection.editVisualTooltip"
                  />
                </div>
              )}
            </motion.div>
          </div>

          <div className="lg:w-3/5 xl:w-7/12 p-6 md:p-10 lg:p-12 flex flex-col justify-center">
            {isOwnerView && ownerContext?.userName && (
              <motion.p custom={0} variants={textVariants} initial="hidden" animate="visible" className="text-sm text-slate-500 mb-4 font-medium">
                {t('agent-common:heroSection.welcomeBack', { userName: ownerContext.userName })}
              </motion.p>
            )}

            {data.poweredByBadgeText && (
               <motion.p custom={1} variants={textVariants} initial="hidden" animate="visible" className="text-sm font-semibold text-indigo-600 mb-2">
                {data.poweredByBadgeText}
              </motion.p>
            )}

            <motion.h1 custom={2} variants={textVariants} initial="hidden" animate="visible" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight">
              {data.solutionName}
            </motion.h1>
            
            <motion.p custom={3} variants={textVariants} initial="hidden" animate="visible" className="text-lg sm:text-xl text-slate-600 leading-relaxed mt-3 mb-5 sm:mb-6">
              {data.tagline}
            </motion.p>

            <motion.div custom={4} variants={textVariants} initial="hidden" animate="visible" className="text-sm text-slate-500 mb-6 sm:mb-8 flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span>{t('agent-common:heroSection.byLabel')}:</span>
              {data.creatorAttribution.profileUrl ? (
                <a href={data.creatorAttribution.profileUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-teal-600 hover:text-teal-700 hover:underline flex items-center gap-1" onClick={(e) => { e.preventDefault(); onAction('VIEW_CREATOR_PROFILE', { url: data.creatorAttribution.profileUrl }); }}>
                  {data.creatorAttribution.name} <ExternalLink size={12} className="opacity-70" />
                </a>
              ) : ( <span className="font-semibold text-slate-700">{data.creatorAttribution.name}</span> )}
              <span className="text-slate-400">•</span>
              <span>{t('agent-common:heroSection.versionLabel')}:</span>
              <span className="font-semibold text-slate-700">{data.solutionVersion}</span>
            </motion.div>

            {/* --- FIX #1: Added conditional check for optional `impactParity` data --- */}
            {data.impactParity && (
              <motion.div custom={5} variants={textVariants} initial="hidden" animate="visible" className="bg-emerald-50 border-2 border-emerald-500 rounded-xl p-4 mb-6 sm:mb-8 flex items-start gap-3 shadow-[3px_3px_0_0_rgba(5,150,105,0.4)]">
                {renderIcon('heart', { size: 32, className: "text-emerald-600 shrink-0 mt-0.5" })}
                <div>
                  <p className="text-sm text-emerald-800 font-medium leading-relaxed">
                    <span className="font-bold">
                      {t('agent-common:heroSection.ethicalContributionPart1', { percentage: data.impactParity.percentage })}
                    </span>
                    {' '}
                    {t('agent-common:heroSection.ethicalContributionPart2', { target: data.impactParity.beneficiaryOrCause })}.
                    {' '}
                    <a href={data.impactParity.learnMoreUrl} target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline text-emerald-600 hover:text-emerald-700" onClick={(e) => { e.preventDefault(); onAction('LEARN_MORE_ETHICAL_IMPACT', { url: data.impactParity?.learnMoreUrl }); }}>
                      {t('agent-common:common.learnMore')}
                    </a>
                  </p>
                </div>
              </motion.div>
            )}

            <motion.p custom={6} variants={textVariants} initial="hidden" animate="visible" className="text-md text-slate-600 leading-relaxed mb-8 sm:mb-10">
              {data.shortDescription}
            </motion.p>
            
            {data.keyBadges && data.keyBadges.length > 0 && (
              <motion.div custom={7} variants={textVariants} initial="hidden" animate="visible" className="flex flex-wrap gap-2.5 sm:gap-3 mb-8 sm:mb-10">
                {data.keyBadges.map((badge, index) => (
                  <Badge 
                    key={index} 
                    textKey={`solution_showcase:${badge.textKey}`}
                    icon={renderIcon(badge.icon)}
                    accentColor={badge.accentColor}
                  />
                ))}
              </motion.div>
            )}

            <motion.div custom={8} variants={textVariants} initial="hidden" animate="visible" className="mt-auto flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              {data.primaryCta && (
                <Button
                  textKey={`solution_showcase:${data.primaryCta.textKey}`}
                  ariaLabelKey={`solution_showcase:${data.primaryCta.ariaLabelKey}`}
                  variant="primary"
                  size="lg"
                  onClick={() => onAction(data.primaryCta.actionType, data.primaryCta.actionPayload)}
                  className="flex-1 sm:flex-auto !py-3.5"
                />
              )}
              {/* --- FIX #2: Added conditional check for optional `secondaryCta` data --- */}
              {data.secondaryCta && (
                <Button
                  textKey={`solution_showcase:${data.secondaryCta.textKey}`}
                  ariaLabelKey={`solution_showcase:${data.secondaryCta.ariaLabelKey}`}
                  variant="secondary"
                  size="lg"
                  onClick={() => onAction(data.secondaryCta!.actionType, data.secondaryCta!.actionPayload)}
                  className="flex-1 sm:flex-auto !py-3.5"
                />
              )}
            </motion.div>
          </div>
        </div>
      </section>
    );
  }
);

SolutionHeroSection.displayName = 'SolutionHeroSection';